import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../../Layout/DashboardLayout";
import DashboardTopbar from "../../Layout/DashboardTopbar";
import useTokenBalance from "../../hooks/useTokenBalance";
import { useAuthContext } from "../../Context/AuthContext";
import { useAlertContext } from "../../Context/AlertContext";
import { API_GET_MODELS_URI, DEFAULT_CONFIG } from "../../Context/Constants";
import {
  getPersistedProfileUiState,
  saveProfilePersistence
} from "../../utils/profilePersistence";
import "./profile-dashboard.css";

const MAX_FIELD_LENGTH = 150;
const persistedUi = getPersistedProfileUiState();

const PROFILE_FORM_FIELDS = [
  "name",
  "email",
  "phone",
  "mobile",
  "street",
  "country_id",
  "state_id",
  "city",
  "zip",
  "website",
  "vat",
  "is_company",
  "x_userlanguage"
];

const mapProfileToFormValues = (profileData) => {
  if (!profileData) return {};

  return PROFILE_FORM_FIELDS.reduce(
    (prev, field) => ({
      ...prev,
      [field]: Array.isArray(profileData[field])
        ? profileData[field][0]
        : profileData[field]
    }),
    {}
  );
};

const getProfileEmail = (profileData) => {
  if (!profileData?.email) return "";
  return Array.isArray(profileData.email) ? profileData.email[0] : profileData.email;
};

const isUnauthorizedError = (error) =>
  error?.response?.status === 401 || error?.request?.status === 401;

const ProfileTextField = ({ id, labelKey, field, value, disabled, onChange, t }) => (
  <div className="form-group">
    <label className="form-label" htmlFor={id}>
      {t(labelKey)}
    </label>
    <div className="profile-field-wrap">
      <input
        id={id}
        name={id}
        type="text"
        className="form-control"
        maxLength={MAX_FIELD_LENGTH}
        value={value || ""}
        disabled={disabled}
        onChange={(e) => onChange(field, e.target.value)}
      />
      {!disabled ? (
        <span className="profile-char-count">{MAX_FIELD_LENGTH - (value?.length || 0)}</span>
      ) : null}
    </div>
  </div>
);

const ProfileSelectField = ({ id, labelKey, field, value, options, onChange, t }) => (
  <div className="form-group">
    <label className="form-label" htmlFor={id}>
      {t(labelKey)}
    </label>
    <select
      id={id}
      name={id}
      className="form-control"
      value={value || ""}
      onChange={(e) => onChange(field, parseInt(e.target.value, 10))}>
      <option value="">{t("Select")}</option>
      {options.map(([key, optionLabel]) => (
        <option key={key} value={key}>
          {optionLabel}
        </option>
      ))}
    </select>
  </div>
);

const Profile = () => {
  const { t } = useTranslation("common");
  const [formValues, setFormValues] = useState(persistedUi.formValues);
  const [submitting, setSubmitting] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(!persistedUi.hasCache);
  const [refreshing, setRefreshing] = useState(false);
  const [countries, setCountries] = useState(persistedUi.countries);
  const checkboxRef = useRef();
  const [states, setStates] = useState(persistedUi.states);
  const { setError, setSuccess } = useAlertContext();
  const { profile, updateOdooProfile, createOdooProfile, searchOdooProfile, user, fetching, loggedIn, refreshSession } =
    useAuthContext();
  const { tokenBalance } = useTokenBalance();

  const navigate = useNavigate();
  const userEmail = user?.attributes?.email;
  const authReady = !fetching && loggedIn && Boolean(userEmail);

  const runWithAuthRetry = useCallback(
    async (request) => {
      try {
        return await request();
      } catch (error) {
        if (isUnauthorizedError(error)) {
          await refreshSession();
          return await request();
        }
        throw error;
      }
    },
    [refreshSession]
  );

  useEffect(() => {
    if (fetching) return undefined;

    if (!loggedIn || !userEmail) {
      setLoadingProfile(false);
      setRefreshing(false);
      return undefined;
    }

    if (profile) {
      setLoadingProfile(false);
      setRefreshing(false);
      return undefined;
    }

    if (!authReady) return undefined;

    let cancelled = false;
    const hasCache = persistedUi.hasCache;

    const loadProfile = async () => {
      if (hasCache) {
        setRefreshing(true);
      } else {
        setLoadingProfile(true);
      }

      try {
        await runWithAuthRetry(() => searchOdooProfile(userEmail));
      } catch (e) {
        if (!cancelled && !hasCache) {
          setError(e.message);
        } else if (!cancelled) {
          console.warn("Background profile refresh failed:", e.message);
        }
      } finally {
        if (!cancelled) {
          setLoadingProfile(false);
          setRefreshing(false);
        }
      }
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [
    authReady,
    fetching,
    loggedIn,
    userEmail,
    profile,
    searchOdooProfile,
    runWithAuthRetry,
    setError
  ]);

  useEffect(() => {
    if (profile) {
      setFormValues(mapProfileToFormValues(profile));
      return;
    }

    if (userEmail) {
      setFormValues((prev) => ({ ...prev, email: userEmail }));
    }
  }, [profile, userEmail]);

  useEffect(() => {
    saveProfilePersistence({ formValues, countries, states });
  }, [formValues, countries, states]);

  useEffect(() => {
    if (!authReady || !profile?.id) return undefined;

    const lang = localStorage.getItem("lang") || "en";
    const currentLang = Array.isArray(profile.x_userlanguage)
      ? profile.x_userlanguage[0]
      : profile.x_userlanguage;
    if (currentLang === lang) return undefined;

    let cancelled = false;

    const syncLanguage = async () => {
      try {
        await updateOdooProfile(profile.id, { x_userlanguage: lang });
      } catch (e) {
        if (!cancelled) {
          console.error("Failed to sync profile language", e);
        }
      }
    };

    syncLanguage();
    return () => {
      cancelled = true;
    };
  }, [authReady, profile?.id, profile?.x_userlanguage, updateOdooProfile]);

  const fetchCountries = async () => {
    const fields = ["display_name", "id"];
    return axios
      .post(API_GET_MODELS_URI, {
        configName: DEFAULT_CONFIG,
        model: "res.country",
        fields: [["id", ">", 0]],
        outputrecords: { fields, limit: 1000 }
      })
      .then((resp) => {
        setCountries(resp.data);
        return resp.data;
      });
  };

  const fetchStates = async (country_id) => {
    const fields = ["display_name", "id"];
    return axios
      .post(API_GET_MODELS_URI, {
        configName: DEFAULT_CONFIG,
        model: "res.country.state",
        fields: [["country_id", "=", country_id]],
        outputrecords: { fields, limit: 1000 }
      })
      .then((resp) => {
        setStates(resp.data);
        return resp.data;
      });
  };

  useEffect(() => {
    if (!authReady) return undefined;

    let cancelled = false;
    const hasCache = countries.length > 0;

    const loadCountries = async () => {
      try {
        await runWithAuthRetry(() => fetchCountries());
      } catch (e) {
        if (!cancelled && !hasCache) {
          setError(e.message);
        } else if (!cancelled) {
          console.warn("Background countries refresh failed:", e.message);
        }
      }
    };

    loadCountries();
    return () => {
      cancelled = true;
    };
  }, [authReady, runWithAuthRetry, setError]);

  useEffect(() => {
    if (!authReady) return undefined;

    const { country_id } = formValues;
    if (!country_id) {
      setStates([]);
      return undefined;
    }

    let cancelled = false;
    const hasCache = states.length > 0;

    const loadStates = async () => {
      try {
        await runWithAuthRetry(() => fetchStates(country_id));
      } catch (e) {
        if (!cancelled && !hasCache) {
          setError(e.message);
        } else if (!cancelled) {
          console.warn("Background states refresh failed:", e.message);
        }
      }
    };

    loadStates();
    return () => {
      cancelled = true;
    };
  }, [authReady, formValues.country_id, runWithAuthRetry, setError]);

  const handleFormChange = (field, value) => {
    setFormValues({ ...formValues, [field]: value });
  };

  const handleCheckBox = () => {
    setFormValues({ ...formValues, is_company: checkboxRef.current.checked });
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSubmitting(true);
      try {
        if (profile) {
          await updateOdooProfile(profile.id, formValues);
          setSuccess("Profile updated successfully!");
        } else {
          await createOdooProfile(formValues);
          setSuccess("Profile created successfully!", true);
          navigate("/home");
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setSubmitting(false);
      }
    },
    [profile, formValues, updateOdooProfile, createOdooProfile, setSuccess, setError, navigate]
  );

  const isBlockingLoad =
    !profile && !persistedUi.hasCache && (fetching || loadingProfile || !authReady);
  const showRefreshing =
    refreshing || (persistedUi.hasCache && !profile && (fetching || loadingProfile));

  const countryOptions = useMemo(() => {
    return countries.map(({ id, display_name }) => [id, display_name]);
  }, [countries]);

  const stateOptions = useMemo(() => {
    return states.map(({ id, display_name }) => [id, display_name]);
  }, [states]);

  return (
    <DashboardLayout>
      <div className="dashboard-page profile-page">
        <DashboardTopbar title={t("My Account")} tokenBalance={tokenBalance} />

        <div className="page-content">
          <div className="page-header">
            <h1>{profile ? t("Edit Profile") : t("Create Profile")}</h1>
            <p>{t("Manage your account details and contact information")}</p>
            {showRefreshing ? (
              <p className="dashboard-loading" style={{ marginTop: 8 }}>
                {t("Fetching...")}
              </p>
            ) : null}
          </div>

          <div className="profile-card">
            {isBlockingLoad ? (
              <div className="dashboard-loading">{t("Fetching...")}</div>
            ) : (
              <form className="profile-form-grid" onSubmit={handleSubmit}>
                <ProfileTextField
                  id="name"
                  labelKey="Name"
                  field="name"
                  value={formValues.name}
                  onChange={handleFormChange}
                  t={t}
                />
                <ProfileTextField
                  id="email"
                  labelKey="Email"
                  field="email"
                  value={formValues.email}
                  disabled
                  onChange={handleFormChange}
                  t={t}
                />
                <ProfileTextField
                  id="phone"
                  labelKey="Phone"
                  field="phone"
                  value={formValues.phone}
                  onChange={handleFormChange}
                  t={t}
                />
                <ProfileTextField
                  id="mobile"
                  labelKey="Mobile"
                  field="mobile"
                  value={formValues.mobile}
                  onChange={handleFormChange}
                  t={t}
                />
                <ProfileSelectField
                  id="country"
                  labelKey="Country"
                  field="country_id"
                  value={formValues.country_id}
                  options={countryOptions}
                  onChange={handleFormChange}
                  t={t}
                />
                <ProfileSelectField
                  id="state"
                  labelKey="State"
                  field="state_id"
                  value={formValues.state_id}
                  options={stateOptions}
                  onChange={handleFormChange}
                  t={t}
                />
                <ProfileTextField
                  id="city"
                  labelKey="City"
                  field="city"
                  value={formValues.city}
                  onChange={handleFormChange}
                  t={t}
                />
                <ProfileTextField
                  id="street"
                  labelKey="Street"
                  field="street"
                  value={formValues.street}
                  onChange={handleFormChange}
                  t={t}
                />
                <ProfileTextField
                  id="zip"
                  labelKey="Zip"
                  field="zip"
                  value={formValues.zip}
                  onChange={handleFormChange}
                  t={t}
                />
                <ProfileTextField
                  id="website"
                  labelKey="Website Link"
                  field="website"
                  value={formValues.website}
                  onChange={handleFormChange}
                  t={t}
                />
                <ProfileTextField
                  id="vat"
                  labelKey="Vat"
                  field="vat"
                  value={formValues.vat}
                  onChange={handleFormChange}
                  t={t}
                />
                <div className="profile-checkbox-row">
                  <input
                    id="is_company"
                    type="checkbox"
                    name="is_company"
                    checked={Boolean(formValues.is_company)}
                    ref={checkboxRef}
                    onChange={handleCheckBox}
                  />
                  <label className="form-label" htmlFor="is_company">
                    {t("Company")}
                  </label>
                </div>

                <div className="profile-form-actions">
                  <button
                    type="submit"
                    className="btn btn-gradient"
                    disabled={submitting || isBlockingLoad}>
                    {t("Save")}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Profile;
